import moment from 'moment';
import { images } from '../../images';
import { BookingSeatsComponent } from '../components/booking/booking-seats/booking-seats.component';
import { ServiceTagComponent } from '../components/service-tag/service-tag.component';
import { APP } from '../config/app';
import {
  IBookingCreate, IPreferredSeat, IRoute, IRouteDetails, IService, IServiceAvailability, IServiceInfo, IStop, ITown,
  IUser, IUserRegular, isOutlineDrawn
} from '../models';
import { IBookingDetails } from '../models/booking/bookingDetails';
import {
  ExpeditionType,
  InputType,
  isDoor2DoorExpeditionType,
  SingleStopReservationType,
  UserRole,
  UserSortType,
  UserStatus,
  UserType,
} from '../models/enums';
import { LiteralService, RouteService, TownsService, UsersService } from '../services';
import { ServiceInfoService } from '../services/service-info/service-info.service';
import { formCreationData, formData } from './bookingData';
import { IRoutePossible } from '../models/route/routePossible';

export async function getBookingForm(booking: IBookingDetails = {} as IBookingDetails, literalService: LiteralService): Promise<unknown[]> {
  const isD2DBooking = isDoor2DoorExpeditionType(booking.tripType);
  const isEditable = (isD2DBooking || booking.tripType == ExpeditionType.DynamicWithFixedPoints) ? false : booking.editable;
  const data: formData[] = [
    {
      img: images.booking.user,
      title: 'bookings.data.user',
      value: `${booking.firstName} ${booking.lastName}`,
      expanded: false
    },
    {
      img: images.booking.service,
      title: 'bookings.data.service',
      component: ServiceTagComponent,
      value: booking.service,
      expanded: false
    },
    {
      img: images.origin,
      title: 'bookings.data.originStop',
      value: `${booking.origin?.time} - ${booking.origin?.name}`,
      setColor: false,
      expanded: false,
      edit: isEditable && {
        inputType: InputType.SELECT_SEARCH,
        values: stops,
        valueToChange: 'origin',
      },
    },
    {
      img: images.destination,
      title: 'bookings.data.destinationStop',
      value: `${booking.destination?.time} - ${booking.destination?.name}`,
      setColor: false,
      expanded: false,
      edit: isEditable && {
        inputType: InputType.SELECT_SEARCH,
        values: stops,
        valueToChange: 'destination',
      }
    },
    {
      img: images.booking.availability,
      title: 'bookings.data.availability',
      value: booking.availability?.dateTime && formatDate(booking.availability.dateTime, literalService),
      expanded: false,
      edit: isEditable && {
        inputType: InputType.SELECT_SEARCH,
        values: availabilities,
        valueToChange: 'availability',
      },
    },
    {
      img: images.booking.seats,
      title: 'bookings.data.seats',
      component: BookingSeatsComponent,
      value: booking,
      expanded: false,
      edit: !(isD2DBooking || booking.tripType == ExpeditionType.DynamicWithFixedPoints) && {
        inputType: InputType.SEATS,
        values: 0,
        valueToChange: 'seats',
      },
    },
    {
      img: images.booking.searchCriteria,
      title: 'bookings.data.searchCriteria',
      value: `bookings.data.${booking.searchCriteria}`,
      expanded: false,
      translateValue: true,
    },
    {
      img: images.clock,
      title: 'bookings.data.requestedTime',
      value: booking.requestedTime ? `${booking.requestedTime}` : '-',
      expanded: false,
      extraInfo: {
        text: 'bookings.data.requestedTimeDiff',
        value: booking.requestedTimeDiff,
      }
    },
    {
      img: images.clock,
      title: 'bookings.data.initialOriginTime',
      value: booking.initialOriginTime ? `${booking.initialOriginTime}` : '-',
      expanded: false,
      extraInfo: {
        text: 'bookings.data.originCommunicatedTime',
        value: booking.originCommunicatedTime,
      }
    },
    {
      img: images.clock,
      title: 'bookings.data.initialDestinationTime',
      value: booking.initialDestinationTime
        ? `${booking.initialDestinationTime}`
        : '-',
      expanded: false,
      extraInfo: {
        text: 'bookings.data.destinationCommunicatedTime',
        value: booking.destinationCommunicatedTime,
      }
    },
    {
      img: images.booking.smartPhone,
      title: 'bookings.data.channel',
      value: `${booking.channel}`,
      expanded: false,
    },
    {
      img: images.booking.notifications,
      title: 'bookings.data.notified',
      value: `bookings.data.notified${booking.isNotified || 'false'}`,
      expanded: false,
      translateValue: true,
    },
    {
      img: images.booking.vehicle,
      title: 'bookings.data.expeditionVehicle',
      value: booking.vehicle?.name,
      expanded: true,
    },
    {
      img: images.booking.driver,
      title: 'bookings.data.expeditionDriver',
      value: booking.driver?.firstName + ' '  + booking.driver?.lastName,
      expanded: true,
    },
    {
      img: images.booking.pickUpTime,
      title: 'bookings.data.originPassingTime',
      value: booking.originPassingTime,
      expanded: true,
      extraInfo: {
        text: 'bookings.data.originConfirmedTime',
        value: booking.originConfirmedTime,
      }
    },
    {
      img: images.booking.dropOffTime,
      title: 'bookings.data.destinationPassingTime',
      value: booking.destinationPassingTime,
      expanded: true,
      extraInfo: {
        text: 'bookings.data.destinationConfirmedTime',
        value: booking.destinationConfirmedTime,
      }
    },
    {
      img: images.booking.feedback,
      title: 'bookings.data.feedback',
      value: booking.feedback ? `bookings.data.feedback${booking.feedback?.positive || 'false'}` : '-',
      expanded: true,
      translateValue: true,
      extraInfo: {
        text: booking.feedback,
        value: `bookings.tooltips.feedback.${booking.feedback?.option.id}`,
        image: images.booking.feedback,
      }
    },
    {
      img: images.distanceBooking,
      title: 'bookings.actions.updateKms.distance',
      value: booking.kms && booking.kms.toFixed(2),
      expanded: true,
      translateValue: false,
      edit: {
        inputType: InputType.NUMBER,
        values: booking.kms,
        valueToChange: 'kms',
      },
    },
  ];
  return data;
}


export async function getCancelledBookingForm(booking: IBookingDetails = {} as IBookingDetails): Promise<unknown[]> {
  const data: formData[] = [
    {
      img: images.booking.user,
      title: 'bookings.data.user',
      value: `${booking.firstName} ${booking.lastName}`,
      expanded: false
    },
    {
      img: images.booking.service,
      title: 'bookings.data.service',
      component: ServiceTagComponent,
      value: booking.service,
      expanded: false
    },
    {
      img: images.origin,
      title: 'bookings.data.origin',
      setColor: false,
      value: booking.origin?.name ?? '-',
      expanded: false
    },
    {
      img: images.destination,
      title: 'bookings.data.destination',
      setColor: false,
      value:  booking.destination?.name ?? '-',
      expanded: false
    },
    {
      img: images.booking.availability,
      title: 'bookings.data.availability',
      value: undefined,
      expanded: false
    },
    {
      img: images.booking.seats,
      title: 'bookings.data.seats',
      component: BookingSeatsComponent,
      value: booking,
      expanded: false
    },
    {
      img: images.booking.searchCriteria,
      title: 'bookings.data.searchCriteria',
      value: undefined,
      expanded: false,
      translateValue: true,
    },
    {
      img: images.clock,
      title: 'bookings.data.requestedTime',
      value:'-',
      expanded: false
    },
    {
      img: images.clock,
      title: 'bookings.data.initialOriginTime',
      value: booking.initialOriginTime ? `${booking.initialOriginTime}` : '-',
      expanded: false
    },
    {
      img: images.clock,
      title: 'bookings.data.initialDestinationTime',
      value: undefined,
      expanded: false
    },
    {
      img: images.booking.smartPhone,
      title: 'bookings.data.channel',
      value: undefined,
      expanded: false
    },
    {
      img: images.booking.notifications,
      title: 'bookings.data.notified',
      value: undefined,
      expanded: false,
      translateValue: true,
    },
    {
      img: images.booking.vehicle,
      title: 'bookings.data.expeditionVehicle',
      value: undefined,
      expanded: true,
    },
    {
      img: images.booking.driver,
      title: 'bookings.data.expeditionDriver',
      value: undefined,
      expanded: true,
    },
    {
      img: images.booking.pickUpTime,
      title: 'bookings.data.originPassingTime',
      value: undefined,
      expanded: true,
    },
    {
      img: images.booking.dropOffTime,
      title: 'bookings.data.destinationPassingTime',
      value: undefined,
      expanded: true,
    },
    {
      img: images.booking.feedback,
      title: 'bookings.data.feedback',
      value: '-',
      translateValue: true,
      expanded: true,
    },
    {
      img: images.distance,
      title: 'bookings.actions.updateKms.distance',
      value: 0.00,
      translateValue: false,
      expanded: true,
    },
  ];
  return data;
}

let users: unknown[] = [];
let routes: unknown[] = [];
let availabilities: unknown[] = [];
let page = 0;

let usersSearched: unknown[] = [];
let pageSearched = 0;

let towns: unknown[] = [];
let stops: any[] = [];
let stopsBooking: any[] = [];
let destinations: any[] = [];

export async function getUsers(usersService: UsersService, userSelected: IUserRegular) {
  await usersService.getUsers(UserSortType.ALPHABETICALLY, page, 10).then((res) => {
    res.content.map((user: IUserRegular) => {
      if (user.status?.toUpperCase() === UserStatus.ACTIVE || user.status?.toUpperCase() === UserStatus.BLOCKED) {
        const extraInfo = user && (user.dni && user.dni || user.phone && user.phone || user.email && user.email);
        users.push(
          {
            id: user.id,
            value: user,
            label:
              user.firstName === null && user.lastName === null ? `${user.email}` : `${user.firstName} ${user.lastName} (${extraInfo})`
          }
        );
      }
    });
  });

  const exists = userSelected && users.find((user: any) => user.id === userSelected.id);
  if (!exists) {
    const extraInfo = userSelected && (userSelected.dni && userSelected.dni || userSelected.phone && userSelected.phone || userSelected.email && userSelected.email);
    userSelected && users.unshift(
      {
        id: userSelected.id,
        value: userSelected,
        label:
          userSelected.firstName === null && userSelected.lastName === null ? `${userSelected.email}` : `${userSelected.firstName} ${userSelected.lastName} (${extraInfo})`
      });
  }
}
export async function getAvailabilities(routeService: RouteService, literalService: LiteralService, booking: IBookingDetails) {
  availabilities = [];
  await routeService.getAvailabilities(booking.routeId, 0, 100, booking.availability.id).then((res) => {
    res.content.map((availability: IServiceAvailability) => {
      availabilities.push(
        {
          id: availability.id,
          value: availability.id,
          label: formatDate(availability.startDateTime, literalService)
        }
      );
    });
  });
  return availabilities;
}

export async function getServiceInfoRoutes(serviceInfoService: ServiceInfoService, booking: IBookingDetails) {
  routes = [];
  await serviceInfoService.getPossibleRoutes(booking.service.id!, [booking.id!!]).then((res) => {
    res.map((route: IRoutePossible) => {
      if (route.possible) {
        routes.push(
          {
            id: route.id,
            value: route.id,
            label: `${route.name}`
          }
        );
      }
    });
  });
  return routes;
}

export async function getStops(townsService: TownsService, booking: IBookingDetails, originStopId?: number) {
  stops = [];
  await townsService.getStopsFrom(booking.service.town.id!, originStopId ?? undefined, booking.availability.startDateTime, false, booking.routeId).then((res) => {
      res.map((stop: any) =>{
        stops.push(
          {
            id: stop.id,
            value: stop.id,
            label: stop.name,
            latitude: stop.latitude,
            longitude: stop.longitude
          }
        );
      });
    });
  stops.sort((a, b) => {
    if (a.label < b.label) {
      return -1;
    } else if (a.label > b.label) {
      return 1;
    }
    return 0;
  });
  return stops;
}


export async function searchUser(usersService: UsersService, showMoreUsers: boolean, value: string) {
  await usersService.getUsers(UserSortType.ALPHABETICALLY, pageSearched, 10, value).then((res) => {
    usersSearched = [];
    res.content.map((user: IUserRegular) => {
      if (user.status?.toUpperCase() === UserStatus.ACTIVE || user.status?.toUpperCase() === UserStatus.BLOCKED) {
        const extraInfo = user && (user.dni && user.dni || user.phone && user.phone || user.email && user.email);
        usersSearched.push({
          id: user.id,
          value: user,
          label:
            user.firstName === null && user.lastName === null ? `${user.email}` : `${user.firstName} ${user.lastName} (${extraInfo})`
        });
      }
    });
  });
}

export class UserBookingData {
  showMoreUsers: boolean;
  search?: boolean;
  value?: string;

  constructor(data: Partial<UserBookingData>) {
    Object.assign(this, data);
  }
}

export async function getClientsTowns(usersService: UsersService, user: IUser) {
  return await usersService.getTownsByUser(user.id!).then((res: any) => {
    const towns: any[] = [];
    res.map((town: ITown) => {
      towns.push({ id: town.id, value: town.id, label: town.name });
    });
    return { towns: towns };
  });
}

// Function to format the date
export function formatDate(dateString: any, literalService: LiteralService): string {
  const [year, month, day, hours, minutes, seconds] = dateString.split(/[-T:]/);
  const date = new Date(Date.UTC(
    parseInt(year),
    parseInt(month) - 1, // Months are 0-based in JavaScript
    parseInt(day),
    parseInt(hours),
    parseInt(minutes),
    parseInt(seconds)
  ));


  const daysOfWeek = ["calendar.weekDays.sun", "calendar.weekDays.mon", "calendar.weekDays.tue", "calendar.weekDays.wed", "calendar.weekDays.thu", "calendar.weekDays.fri", "calendar.weekDays.sat"];
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    throw new TypeError("Invalid date object");
  }
  const dayOfWeek = literalService.get(daysOfWeek[date.getDay()]);
  return `${hours}:${minutes} - ${dayOfWeek} - ${day}/${month}/${year}`;
}

export function getRepeatBooking(literalService?: LiteralService): unknown[] {
  return [
    { id: "everyWeek", value: 1, label: literalService!.get("bookings.everyWeek", true) },
    { id: "every2Weeks", value: 2, label: literalService!.get("bookings.every2Weeks", true) },
  ];
}

export function getDaysOfWeek(literalService?: LiteralService, booking?: IBookingCreate): unknown[] {
  const weekDays = [
    { id: 'monday', name: literalService!.get("calendar.longWeekDays.mon", true), code: 'monday' },
    { id: 'tuesday', name: literalService!.get("calendar.longWeekDays.tue", true), code: 'tuesday' },
    { id: 'wednesday', name: literalService!.get("calendar.longWeekDays.wed", true), code: 'wednesday' },
    { id: 'thursday', name: literalService!.get("calendar.longWeekDays.thu", true), code: 'thursday' },
    { id: 'friday', name: literalService!.get("calendar.longWeekDays.fri", true), code: 'friday' },
    { id: 'saturday', name: literalService!.get("calendar.longWeekDays.sat", true), code: 'saturday' },
    { id: 'sunday', name: literalService!.get("calendar.longWeekDays.sun", true), code: 'sunday' },
  ];

  return booking
    ? weekDays.filter(weekDay => booking.scheduler && booking.scheduler.daysOfWeek?.includes(weekDay.id.toUpperCase()))
    : weekDays;
}

export async function newBookingForm(booking: IBookingCreate = {} as IBookingCreate, usersService: UsersService, literalService: LiteralService, userData?: UserBookingData, serviceInfos?: IServiceInfo[], stops?: unknown[], changes?: any, favouriteOrRebookSingle: boolean = false, isNewFavourite: boolean = false): Promise<unknown[]> {
  const isD2D = serviceInfos ? isDoor2DoorService(serviceInfos) : false;
  const isTripPlanner = serviceInfos ? isTripPlannerService(serviceInfos) : false;

  if (userData) {
    if (userData.search) {
      if (userData.showMoreUsers) {
        pageSearched += 1;
      } else {
        users = [];
        page = 0;
        usersSearched = [];
        pageSearched = 0;
      }
      await searchUser(usersService, userData.showMoreUsers, userData.value!);
    } else {
      page += 1;
      await getUsers(usersService, booking.user);
    }
  }

  if (changes && changes.includes('user')) {
    towns = [];
    stopsBooking = [];
  } else if (changes && changes.includes('town')) {
    stopsBooking = [];
    destinations = [];
    booking.originStopId = 0;
    booking.destinationStops = [];
    booking.originStop = null;
    booking.destinationStop = null;
  } else if (changes && changes.includes('stop') || changes && changes.includes('destinationsStops')) {
    destinations = [];
  } else if (changes && changes.includes('originStopId')) {
    stopsBooking = [];
  }

  // NOTE: TOWNS
  if (!towns.length && booking.targetUserId) {
    await getClientsTowns(usersService, booking.user).then((res) => {
      towns = res.towns;
    });
  }

  // [DISABLED] singleStopReservation feature
  // const isSingleReservation = serviceInfos && serviceInfos?.some((serviceInfo: IServiceInfo) => serviceInfo.singleStopReservations);
  // const isSingleReservation = false;
  // let singleStopReservationType;
  // if (!favouriteOrRebookSingle) {
  //   if (serviceInfos && serviceInfos?.length > 0 && changes && changes.includes('stop')) {
  //     singleStopReservationType = SingleStopReservationType.ORIGIN;
  //   } else if (serviceInfos && serviceInfos?.length > 0 && changes && (changes.includes('destination') || changes.includes(''))) {
  //     singleStopReservationType = booking.destinationStops.length > 0 && booking.destinationStops[0].exitStop.id !== 0 ? SingleStopReservationType.DESTINATION: SingleStopReservationType.ORIGIN;
  //   }
  // }

  if (isD2D) {
    if (changes) {
      let formattedStops: any[] = [];
      stops?.filter((stop: any) => booking.originStop ? stop.name !== booking.originStop.name : true)
      .map((stop: any) => {
        formattedStops.push({
          id: stop.id,
          value: isD2D ? stop : stop.name,
          label: stop.name
        });
      });
      if (changes.includes('destinationsStops') && (isD2D || booking.door2DoorDestinationFreeStops)) {
        destinations = formattedStops;
      } else if (changes.includes('originStopId') && (isD2D || booking.door2DoorOriginFreeStops)) {
        stopsBooking = formattedStops;
      }
    }
    if (booking.door2DoorOriginFreeStops) {
      if (booking.originStop) {
        const stopIndex = stopsBooking.findIndex((stop: any) => stop.value === (booking.originStop?.name));
        if (stopIndex !== -1) {
          stopsBooking[stopIndex!].disabled = true;
        } else {
          stopsBooking.push({
            id: null,
            value: isD2D ? booking.originStop : booking.originStop.name,
            label: booking.originStop.name,
            disabled: true
          });
        }
      }
    }
    if (booking.door2DoorDestinationFreeStops) {
      if (booking.destinationStop) {
        const stopIndex = destinations.findIndex((stop: any) => (stop.value === booking.destinationStop?.name));
        if (stopIndex !== -1) {
          destinations[stopIndex!].disabled = true;
        } else {
          destinations.push({
            id: null,
            value: isD2D ? booking.destinationStop : booking.destinationStop.name,
            label: booking.destinationStop.name,
            disabled: true
          });
        }
      }
    }
  }
  
  // NOTE: ORIGINS
  if (!isD2D || (isD2D && !booking.door2DoorOriginFreeStops)) {
    if (!stopsBooking.length) {
      const originStops = serviceInfos?.flatMap((serviceInfo: IServiceInfo) => serviceInfo.stops) ?? [];
      uniqueStops(originStops)
        .forEach((stop: any) => {
          // [DISABLED] restrictedOrigins feature
          // const unavailable = serviceInfo.restrictedOrigins.some((origin: any) => origin === stop.id);
          // if (!unavailable) {
            stopsBooking.push({ id: stop.id, value: isD2D ? stop : stop.id, label: stop.name });
          // }
        });
      stopsBooking.sort((a, b) => {
        if (a.label < b.label) {
          return -1;
        } else if (a.label > b.label) {
          return 1;
        }
        return 0;
      });
    }
  }

  // NOTE: DESTINATIONS
  if (!isD2D || (isD2D &&!booking.door2DoorDestinationFreeStops)) {
    // [DISABLED] singleStopReservation feature - removed isSingleReservation from condition
    if (destinations.length === 0 || (changes && changes.includes('stop'))) {
      uniqueStops(stops ?? []).forEach((destination: any) => {
        destinations.push({ id: destination.id, value: isD2D ? destination : destination.id, label: destination.name || destination.label });
      });
    }
  }

  let originValue: any;
  let destinationValue: any;
  if (isD2D) {
    if (booking.recurrentBooking) {
      originValue = booking.scheduler.favorite.inStop || booking.favourite?.inStop;
      destinationValue = booking.scheduler.favorite.outStop || booking.favourite?.outStop;
    } else {
      if (booking.originStop) {
        if (!booking.door2DoorOriginFreeStops) {
          originValue = stopsBooking.find((stop: any) => stop.id === booking.originStop.id)?.value;
        } else {
          originValue = booking.originStop;
        }
      } 
      if (booking.destinationStop) {
        if (!booking.door2DoorDestinationFreeStops) {
          destinationValue = destinations.find((stop: any) => stop.id === booking.destinationStop.id)?.value;
        } else {
          destinationValue = booking.destinationStop;
        }
      }
    }
  } else {
    if (booking.recurrentBooking) {
      originValue = booking.scheduler.favorite.inStop?.id;
      destinationValue = booking.scheduler.favorite.outStop?.id;
    } else {
      originValue = isTripPlanner && booking.door2DoorOriginFreeStops ? booking.originStop : booking.originStopId;
      if (isTripPlanner && booking.door2DoorDestinationFreeStops) {
        destinationValue = booking.destinationStop;
      } else if (booking.destinationStops && booking.destinationStops[0] && booking.destinationStops[0].exitStop.id) {
        destinationValue = booking.destinationStops[0].exitStop.id
      }
    }
  }

  // let rangeDate: any[] = [];
  // let rangeData: string = "";
  // if (booking.scheduler && booking.recurrentBooking) {
  //   rangeDate
  //       ? [
  //           moment(booking.scheduler.startDate, 'YYYY-MM-DD').format('DD/MM/YYYY'),
  //           moment(booking.scheduler.endDate, 'YYYY-MM-DD').format(' DD/MM/YYYY'),
  //         ]
  //       : [booking.scheduler.startDate, booking.scheduler.endDate];
    
  //       rangeData = rangeDate.toString().replace(',', ' - ');
  // }

  // const daysOfWeekSelecteds = booking.recurrentBooking && booking.scheduler.daysOfWeek?.map((dayOfWeek: any) => dayOfWeek.id.toUpperCase());

  // booking.recurrentBooking && booking.scheduler.daysOfWeek

  const allSeats = [];
  if (booking.recurrentBooking) {
    const seats = booking.scheduler.favorite.passengers || booking.favourite?.passengers;
    const prmSeats = booking.scheduler.favorite.prmPassengers || booking.favourite?.prmPassengers;

    for (let index = 0; index < seats!; index++) {
      allSeats.push({prm: false});
    }
    for (let index = 0; index < prmSeats!; index++) {
      allSeats.push({prm: true});
    }
  }

  if (serviceInfos) booking.stops = stopsBooking;
  const data: formCreationData[] = [
    {
      img: images.booking.user,
      title: 'bookings.data.user',
      value: booking.user,
      color: false,
      step: 1,
      informativeToNoChange: booking.user ? true : false,
      informativeToNoChangeText: booking.user && booking.user.firstName + ' ' + booking.user.lastName,
      edit: {
        visible: true,
        inputType: InputType.INFINITE_SCROLL,
        values: userData && userData.search ? usersSearched : users,
        valueToChange: 'targetUserId',
      },
    },
    {
      img: images.booking.actions.favorite,
      title: 'bookings.actions.createFavourite.placeholderInput',
      value: booking.favouriteName ?? '',
      color: false,
      step: 2,
      edit: {
        visible: isNewFavourite ? true : false,
        inputType: InputType.TEXT,
        valueToChange: 'favouriteName',
      },
    },
    {
      img: images.booking.recurrent,
      title: 'bookings.data.recurrentName',
      value: booking.scheduler && booking.scheduler.name || '',
      color: false,
      step: 2,
      edit: {
        visible: booking.recurrentBooking || false,
        inputType: InputType.TEXT,
        valueToChange: 'scheduler.name',
      },
    },
    // {
    //   img: images.setup.since,
    //   title: 'setup.data.periodDates',
    //   value: rangeData,
    //   color: false,
    //   step: 2,
    //   edit: {
    //     id: [ 'startDate', 'endDate' ],
    //     formControlName: [ 'startDate', 'endDate' ],
    //     visible: booking.recurrentBooking || false,
    //     inputType: InputType.RANGE_DATE,
    //     disabled: [false, booking.scheduler && booking.scheduler.startDate === null],
    //     required: true,
    //     valueToChange: [ 'startDate', 'endDate' ],
    //   },
    // },
    {
      img: images.booking.startDate,
      title: 'bookings.data.startDate',
      value: booking.scheduler && booking.scheduler.startDate ? moment(booking.scheduler.startDate, 'DD/MM/YYYY').isValid() ? moment(booking.scheduler.startDate, 'DD/MM/YYYY').format('YYYY-MM-DD') : moment(booking.scheduler.startDate, 'YYYY-MM-DD').format('YYYY-MM-DD') : '',
      color: false,
      step: 2,
      calendar: {
        showOnTop: false,
        hideCalendar: false,
        showYear: false,
        onlyFutureDays: true,
      },
      edit: {
        id: "startDate",
        visible: booking.recurrentBooking || false,
        inputType: InputType.DATE,
        valueToChange: 'scheduler.startDate',
      },
    },
    {
      img: images.booking.endDate,
      title: 'bookings.data.endDate',
      value: booking.scheduler && booking.scheduler.endDate ? moment(booking.scheduler.endDate, 'DD/MM/YYYY').isValid() ? moment(booking.scheduler.endDate, 'DD/MM/YYYY').format('YYYY-MM-DD') : moment(booking.scheduler.endDate, 'YYYY-MM-DD').format('YYYY-MM-DD') : '',
      color: false,
      step: 2,
      calendar: {
        showOnTop: false,
        hideCalendar: false,
        showYear: false,
        // allDays: true, // TODO: For v2 with range of dates (startDate, endDate)
        onlyFutureDays: true,
      },
      edit: {
        id: "endDate",
        visible: booking.recurrentBooking || false,
        inputType: InputType.DATE,
        valueToChange: 'scheduler.endDate',
      },
    },
    {
      img: images.booking.repeatBooking,
      title: 'bookings.data.repeat',
      value: booking.scheduler && booking.scheduler.repeatEvery,
      color: false,
      step: 2,
      edit: {
        visible: booking.recurrentBooking || false,
        inputType: InputType.SELECT_SEARCH,
        values: getRepeatBooking(literalService),
        valueToChange: 'scheduler.repeatEvery',
      },
    },
    {
      img: images.booking.days,
      title: 'bookings.data.days',
      value: booking.recurrentBooking && booking.scheduler.daysOfWeek,
      array: true,
      // dataToShow: 'code',
      color: false,
      step: 2,
      edit: {
        id:  booking.recurrentBooking && booking.scheduler.daysOfWeek,
        visible: booking.recurrentBooking || false,
        inputType: InputType.MULTISELECT,
        required: false,
        values: getDaysOfWeek(literalService),
        selectedValues: booking.recurrentBooking && booking.scheduler.daysOfWeek,
        valueToChange: 'scheduler.daysOfWeek',
      },
    },
    {
      img: images.booking.area,
      title: 'bookings.data.town',
      value: booking.townId,
      color: false,
      step: 2,
      edit: {
        visible: true,
        inputType: InputType.SELECT_SEARCH,
        disabled: booking.recurrentBooking || booking.favourite?.id,
        values: towns,
        valueToChange: 'town',
      },
    },
    {
      img: images.origin,
      title: 'bookings.data.originStop',
      value: originValue,
      color: false,
      step: 2,
      placeholder: literalService.get('bookings.data.d2dInstructions', true),
      searchPlaceholder: literalService.get('bookings.data.d2dSearchPlaceholder', true),
      edit: {
        visible: true,
        inputType: isD2D && booking.door2DoorOriginFreeStops ? InputType.AUTOCOMPLETE_SEARCH : InputType.SELECT_SEARCH,
        values: stopsBooking,
        customSearchEnabled: booking.door2DoorOriginFreeStops,
        // [DISABLED] singleStopReservation feature - was: (isSingleReservation && singleStopReservationType === SingleStopReservationType.DESTINATION) && !favouriteOrRebookSingle || booking.recurrentBooking,
        disabled: booking.recurrentBooking,
        valueToChange: 'originStopId'
      },
    },
    {
      img: images.destination,
      title: 'bookings.data.destinationStop',
      value: destinationValue,
      color: false,
      step: 2,
      placeholder: literalService.get('bookings.data.d2dInstructions', true),
      searchPlaceholder: literalService.get('bookings.data.d2dSearchPlaceholder', true),
      edit: {
        visible: true,
        inputType: isD2D && booking.door2DoorDestinationFreeStops ? InputType.AUTOCOMPLETE_SEARCH : InputType.SELECT_SEARCH,
        values: destinations,
        customSearchEnabled: booking.door2DoorDestinationFreeStops,
        // [DISABLED] singleStopReservation feature - was: ((isSingleReservation && singleStopReservationType === SingleStopReservationType.ORIGIN) && !favouriteOrRebookSingle) || (!isSingleReservation && !booking.originStop && !booking.destinationStop) || booking.recurrentBooking,
        disabled: (!booking.originStop && !booking.destinationStop) || booking.recurrentBooking,
        valueToChange: 'destinationsStops'
      },
    },
    {
      value: booking.recurrentBooking ? allSeats : booking.destinationStops,
      color: false,
      step: 2,
      edit: {
        visible: true,
        inputType: InputType.SEATS,
        values: booking.destinationStops,
        valueToChange: 'destinationsStops',
        disabled: !booking.destinationStop || booking.recurrentBooking,
        showPrm: serviceInfos?.some((serviceInfo) => serviceInfo.prmSeatsAvailable)
      },
    },
    {
      img: images.booking.date,
      title: 'bookings.data.date',
      value: booking.date,
      color: false,
      step: 2,
      calendar: {
        showOnTop: true,
        onlyFutureDays: true,
        daysInAdvance: booking.serviceInfo?.bookingDaysInAdvance,
        hideCalendar: false,
        showYear: false,
        possibleMultipleDates: !isTripPlanner,
      },
      fullData: booking,
      edit: {
        id: "date",
        visible: isNewFavourite || booking.recurrentBooking ? false : true,
        inputType: InputType.DATE,
        valueToChange: 'date',
      },
    },
    {
      img: images.clock,
      title: 'bookings.data.time',
      value:  booking.recurrentBooking && booking.scheduler ? booking.scheduler.bookingTime : booking.time,
      color: false,
      step: 2,
      edit: {
        visible: true,
        inputType: InputType.TIME,
        valueToChange: booking.recurrentBooking ? 'scheduler.bookingTime' : 'time',
      },
    }
  ];
  return data;
}

export function favouriteStopId(stop?: IStop, origin = true) {
  const freeStopId = origin ? APP.FAVOURITE_FREE_IN_STOP_ID : APP.FAVOURITE_FREE_OUT_STOP_ID;
  return stop?.id === freeStopId ? undefined : stop?.id;
}

export function isDoor2DoorService(serviceInfos: IServiceInfo[]) {
  return serviceInfos.some((serviceInfo: IServiceInfo) =>
    isDoor2DoorExpeditionType(serviceInfo.expeditionType) && !!serviceInfo.outlines?.some(isOutlineDrawn));
}

export function showsDemandStopIcon(stop: IStop, serviceInfo: IServiceInfo) {
  return !!stop.demand && !isDoor2DoorExpeditionType(serviceInfo.expeditionType);
}

export function canBookOutsideOutlines(role?: UserRole) {
  return role === UserRole.ADMIN || role === UserRole.SUPERADMIN;
}

export function uniqueStops(stops: any[]): any[] {
  const stopsByPosition = new Map<string, any>();
  stops?.forEach((stop: any) => {
    const coordinates = stop?.location?.geometry?.coordinates;
    const key = coordinates ? coordinates.join(',') : String(stop?.id);
    if (!stopsByPosition.has(key)) stopsByPosition.set(key, stop);
  });
  return Array.from(stopsByPosition.values());
}

export function isTripPlannerService(serviceInfos: IServiceInfo[]) {
  return serviceInfos.some((serviceInfo: IServiceInfo) => serviceInfo.tripPlanner);
}

export function hasAvailabilityDetail(serviceInfos: IServiceInfo[], serviceInfo?: IService | IServiceInfo, alternative?: boolean) {
  if (isTripPlannerService(serviceInfos)) return true;
  // using addressSearch as walking flag in non d2d services
  const fullServiceInfo = serviceInfos.find((info: IServiceInfo) => info.id === serviceInfo?.id);
  return (!isDoor2DoorService(serviceInfos) && !!fullServiceInfo?.addressSearch) || !!alternative;
}
